import { useState, useMemo } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  HStack,
  Text,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
  Icon,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import type { PlayableLocationDto } from '../api/streamApi';

interface LocationSelectorProps {
  locations: PlayableLocationDto[];
  selectedLocationId: string | null;
  onSelect: (locationId: string) => void;
}

export function LocationSelector({
  locations,
  selectedLocationId,
  onSelect,
}: LocationSelectorProps) {
  const { t } = useTranslation();
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [search, setSearch] = useState('');

  const selectedLocation = locations.find((l) => l.id === selectedLocationId);

  const filtered = useMemo(() => {
    if (!search.trim()) return locations;
    const q = search.toLowerCase();
    return locations.filter((l) => l.name.toLowerCase().includes(q));
  }, [locations, search]);

  const handleSelect = (locationId: string) => {
    onSelect(locationId);
    setSearch('');
    onClose();
  };

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start" matchWidth>
      <PopoverTrigger>
        <Button
          variant="outline"
          onClick={onToggle}
          data-testid="location-selector"
          w="100%"
          justifyContent="space-between"
          fontWeight="normal"
          rightIcon={
            <Icon viewBox="0 0 24 24" boxSize={4}>
              <path fill="currentColor" d="M7 10l5 5 5-5H7z" />
            </Icon>
          }
        >
          {selectedLocation?.name || t('player.selectLocation')}
        </Button>
      </PopoverTrigger>
      <PopoverContent data-testid="location-selector-popover" maxH="300px">
        <PopoverBody p={2}>
          <InputGroup size="sm" mb={2}>
            <InputLeftElement pointerEvents="none">
              <Icon viewBox="0 0 24 24" boxSize={3} color="gray.400">
                <path
                  fill="currentColor"
                  d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
                />
              </Icon>
            </InputLeftElement>
            <Input
              placeholder={t('player.searchLocations')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="location-search-input"
            />
          </InputGroup>

          <VStack align="stretch" spacing={0} overflowY="auto" maxH="220px">
            {filtered.length === 0 ? (
              <Text fontSize="sm" color="gray.500" textAlign="center" py={3}>
                {t('player.noLocationsFound')}
              </Text>
            ) : (
              filtered.map((loc) => {
                const isSelected = loc.id === selectedLocationId;
                return (
                  <HStack
                    key={loc.id}
                    as="button"
                    type="button"
                    onClick={() => handleSelect(loc.id)}
                    data-testid={`location-item-${loc.id}`}
                    px={3}
                    py={2}
                    borderRadius="md"
                    bg={isSelected ? 'blue.50' : 'transparent'}
                    _hover={{ bg: isSelected ? 'blue.100' : 'gray.50' }}
                    justify="space-between"
                    w="100%"
                    cursor="pointer"
                    border="none"
                  >
                    <Box textAlign="left">
                      <Text
                        fontSize="sm"
                        fontWeight={isSelected ? '600' : '400'}
                        color={isSelected ? 'blue.600' : 'gray.800'}
                      >
                        {loc.name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {loc.address}
                      </Text>
                    </Box>
                    {isSelected && (
                      <Icon viewBox="0 0 24 24" boxSize={4} color="blue.500">
                        <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                      </Icon>
                    )}
                  </HStack>
                );
              })
            )}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
